import "./css/SigningFlow.css";
import { STEPS } from "../constants/steps";
import {useEffect,useState} from "react";

export default function DobScreen({
    flowData,
    updateFlowData,
    flowContext,
    updateFlowContext,
    next,
    back,
    loading,
    setLoading,
    error,
    setError,
}) {   


    //since 3 separate fields are required in UI, use this state fields.
    const [month, setMonth] = useState("");
    const [day, setDay] = useState("");
    const [year, setYear] = useState("");

    const signatureNameEdit =  flowData?.profileEditMode === "SIGNATURE_NAME";

    const months = {
            Jan: "01",
            Feb: "02",
            Mar: "03",
            Apr: "04",
            May: "05",
            Jun: "06",
            Jul: "07",
            Aug: "08",
            Sep: "09",
            Oct: "10",
            Nov: "11",
            Dec: "12"
        };
    const monthNames = Object.keys(months);
    const currentYear = new Date().getFullYear();
    const years = Array.from(
        { length: 101 },
        (_, index) => currentYear - index
    );
    const daysInMonth = () => {
        if (!month || !year) {
            return 31;
        }
        const monthIndex = monthNames.indexOf(month) + 1;
        return new Date(
            Number(year),
            monthIndex,
            0
        ).getDate();
    };

    /* If someone selects 31 March and then changes to April then the selected day should automatically clear because April only has 30 days. */
    useEffect(() => {
        if (
            day &&
            Number(day) > daysInMonth()
        ) {
            setDay("");
        }
    }, [month, year]);

    //Pre-populate when editing an existing profile
    useEffect(() => {
        // DOB is not required/displayed when editing
        // only the signature name.
        if (signatureNameEdit) {
            return;
        }
        const dob = flowContext.profile?.dob;
        if (!dob) {
            return;
        }
        const date = new Date(dob);
        setYear(String(date.getFullYear()));
        setMonth(
            monthNames[date.getMonth()]
        );
        setDay(String(date.getDate()));
    }, [flowContext.profile, signatureNameEdit]);

    const days = Array.from(
        { length: daysInMonth() },
        (_, index) => index + 1
    );

    const continueToNext = () => {

        if (loading) return;

        setError("");

        const profile = flowContext.profile;

        if (!profile?.firstName?.trim()) {
            setError("First name is required.");
            return;
        }

        if (!profile?.lastName?.trim()) {
            setError("Last name is required.");
            return;
        }

        /*
        * ---------------------------------------------------------
        * SIGNATURE NAME EDIT
        * ---------------------------------------------------------
        *
        * DOB is not being edited here.
        * Send the existing DOB back because the backend
        * save_profile validation still requires DOB.
        */
        if (signatureNameEdit) {

            next({
                action: "save_only_name",
                profile: {
                    firstName: profile.firstName,
                    lastName: profile.lastName
                }
            });

            return;
        }

        /*
        * ---------------------------------------------------------
        * NORMAL EDIT PROFILE
        * ---------------------------------------------------------
        */

        if (!month || !day || !year) {
            setError("Date of birth is required.");
            return;
        }

        const monthNumber = months[month];

        const dob =
            `${year}-${monthNumber}-${day.padStart(2, "0")}`;

        const parsedDate = new Date(dob);

        if (Number.isNaN(parsedDate.getTime())) {
            setError("Please enter a valid date of birth.");
            return;
        }

        const phone =
            flowContext.customerPhone?.phone ??
            flowData.phone;

        if (!phone) {
            setError("Verified phone number not found.");
            return;
        }

        next({
            action: "save_profile",
            profile: {
                ...profile,
                dob
            }
        });
    };

    return (
        <div className={`page ${loading ? "page-loading" : ""}`}>

            {loading && (
                <div className="loading-overlay">
                    Loading...
                </div>
            )}

            {error && (
                <div className="text-red-600 mt-2">
                    {error}
                </div>
            )}
            
            <div className="page-header">

                <h2 className="business-name">
                    {flowContext.selectedService?.name
                        ? `${flowContext.selectedService.name} at ${flowContext.business?.businessName}`
                        : flowContext.business?.businessName}
                </h2>

            </div>

            <div className="page-content">

                <h3 className="form-title">
                    {signatureNameEdit
                        ? "Correct your name"
                        : "Complete your details"}
                </h3>

               {!signatureNameEdit && (
                    <button 
                        type="button" 
                        className="text-link-button" 
                        onClick={() => 
                            next({ 
                                action: "pick_another_profile" 
                            }) 
                        } 
                    >
                        ← Using a different profile?
                    </button>
                )}

                <div className="form-group">
                    <label>First Name</label>

                    <input
                        type="text"
                        value={flowContext.profile?.firstName ?? ""}
                        onChange={(e)=>{
                            updateFlowContext({
                                profile:{
                                    ...flowContext.profile,
                                    firstName:e.target.value
                                }
                            });
                        }}
                    />
                </div>

                <div className="form-group">
                    <label>Last Name</label>

                    <input
                        type="text"
                        value={flowContext.profile?.lastName ?? ""}
                        onChange={(e)=>{
                            updateFlowContext({
                                profile:{
                                    ...flowContext.profile,
                                    lastName:e.target.value
                                }
                            });
                        }}
                    />
                </div>

                {!signatureNameEdit && (
                    <div className="form-group">
                        <label>Date of Birth</label>

                        <div className="dob-row">

                            <select
                                className="dob-input"
                                value={month}
                                onChange={(e)=>setMonth(e.target.value)}
                            >
                                <option value="">Month</option>

                                {monthNames.map((monthName) => (
                                    <option
                                        key={monthName}
                                        value={monthName}
                                    >
                                        {monthName}
                                    </option>
                                ))}
                            </select>

                            <select
                                className="dob-input"
                                value={day}
                                onChange={(e)=>setDay(e.target.value)}
                            >
                                <option value="">Day</option>

                                {days.map((d) => (
                                    <option
                                        key={d}
                                        value={d}
                                    >
                                        {d}
                                    </option>
                                ))}
                            </select>

                            <select
                                className="dob-input"
                                value={year}
                                onChange={(e)=>setYear(e.target.value)}
                            >
                                <option value="">Year</option>

                                {years.map((y) => (
                                    <option
                                        key={y}
                                        value={y}
                                    >
                                        {y}
                                    </option>
                                ))}
                            </select>

                        </div>                        

                    </div>
                )}

                {!signatureNameEdit && (
                    <div className="form-group">

                        <label>Mobile Number</label>

                        <div className="verified-input">

                            <input
                                type="tel"
                                value={
                                    flowContext.customerPhone?.phone ??
                                    flowData.phone
                                }
                                readOnly
                            />

                            <div className="verified-badge">
                                ✓ Verified
                            </div>

                        </div>

                    </div>
                )}

            </div>

            <div className="page-footer">   

                         

                <button 
                    className="primary-button" 
                    onClick={continueToNext}
                    disabled={loading}
                >
                    {signatureNameEdit ? "Save Name" : "Continue"}
                    <span className="button-arrow">›</span>
                </button>

                <div className="powered-by">
                    Powered by <strong>DidYouSign</strong>
                </div>

            </div>

        </div>
    );
}